// How a sandbox's state is said: the same words in the chat, the previews
// and the operator's list.
//
// The server's row is what is true (`SandboxDTO.status` and `want`); the
// screen only words it. Whether the agent inside has ever reported (its
// device's capabilities) tells getting a new sandbox ready from waking one.
import type {
  SandboxDTO,
  SandboxErrorDTO,
  SandboxProfile,
  SandboxStatus,
  SandboxWant,
} from "@trameria/cloud-api/sandboxes-shared";
import { m } from "@trameria/cloud-i18n";

/** The device's name in lists and pills (the general one is trameria itself). */
export const DEVICE_LABEL: Record<SandboxProfile, string> = {
  // i18n-ignore: the product's name
  general: "trameria",
  // i18n-ignore: a fixed short name, beside the general one
  dev: "dev",
};

/** In sentences ("trameria" alone would read as the app). */
export const nameInText = (profile: SandboxProfile): string =>
  profile === "dev" ? m.sandboxes_name_dev() : m.sandboxes_name_general();

export type Phase =
  | "preparing"
  | "waking"
  | "running"
  | "asleep"
  | "queued"
  | "deleting"
  | "error";

/** What the person sees happening. `reported`: its agent has said what it has, once. */
export function phaseOf(
  s: { status: SandboxStatus; want: SandboxWant },
  reported: boolean,
): Phase {
  if (s.want === "gone" || s.status === "deleting") return "deleting";
  switch (s.status) {
    case "error":
      return "error";
    case "queued":
      return "queued";
    case "running":
      return reported ? "running" : "preparing";
    case "starting":
      return reported ? "waking" : "preparing";
    case "absent":
      if (s.want === "asleep") return "asleep";
      return reported ? "waking" : "preparing";
    default:
      // stopping, stopped
      return "asleep";
  }
}

export function phaseLabel(phase: Phase, queuePosition: number | null) {
  switch (phase) {
    case "preparing":
      return m.sandboxes_status_preparing();
    case "waking":
      return m.sandboxes_status_waking();
    case "running":
      return m.sandboxes_status_running();
    case "asleep":
      return m.sandboxes_status_asleep();
    case "queued":
      return queuePosition !== null
        ? m.sandboxes_status_queued_at({ n: queuePosition })
        : m.sandboxes_status_queued();
    case "deleting":
      return m.sandboxes_status_deleting();
    case "error":
      return m.sandboxes_status_error();
  }
}

const minutesOf = (e: SandboxErrorDTO) => {
  const v = e.params.minutes;
  return typeof v === "number" ? v : Number(v ?? 0);
};

/** A failure in the reader's words. */
export function errorWords(e: SandboxErrorDTO | null): string {
  if (!e) return "";
  switch (e.code) {
    case "k8s_http":
    case "k8s_timeout":
    case "k8s_network":
      return m.sandboxes_error_k8s();
    case "collision":
      return m.sandboxes_error_collision();
    case "no_dev_image":
      return m.sandboxes_error_no_dev_image();
    case "profile_mismatch":
      return m.sandboxes_error_profile_mismatch();
    case "start_timeout":
      return m.sandboxes_error_start_timeout({ minutes: minutesOf(e) });
    case "stop_timeout":
      return m.sandboxes_error_stop_timeout({ minutes: minutesOf(e) });
    case "delete_timeout":
      return m.sandboxes_error_delete_timeout({ minutes: minutesOf(e) });
    case "unreachable":
      return m.sandboxes_error_unreachable({ minutes: minutesOf(e) });
    default:
      return m.sandboxes_error_other({ code: e.code });
  }
}

/** The failure as recorded, for operators (Kubernetes' reason and message included). */
export function errorDetail(e: SandboxErrorDTO): string {
  const params = Object.entries(e.params)
    .filter(([, v]) => v !== null && v !== "")
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(",") : String(v)}`);
  return [e.code, ...params].join(" ");
}

/** The phase's sentence in a chat on this sandbox (null: nothing to say). */
export function chatLineWords(
  s: SandboxDTO,
  phase: Phase,
): { text: string; failed: boolean } | null {
  const name = nameInText(s.profile);
  switch (phase) {
    case "running":
      return null;
    case "preparing":
      return { text: m.sandboxes_line_preparing({ name }), failed: false };
    case "waking":
      return { text: m.sandboxes_line_waking({ name }), failed: false };
    case "asleep":
      return { text: m.sandboxes_line_asleep({ name }), failed: false };
    case "queued":
      return {
        text: m.sandboxes_line_queued({ name, n: s.queuePosition ?? 1 }),
        failed: false,
      };
    case "deleting":
      return { text: m.sandboxes_line_deleting({ name }), failed: false };
    case "error":
      return {
        text: m.sandboxes_line_error({ name, error: errorWords(s.lastError) }),
        failed: true,
      };
  }
}

/** Whether a device's agent has reported what it has (its CLIs), once. */
export function hasReported(device: { capabilities: Record<string, unknown> }) {
  const agents = device.capabilities?.agents;
  return Array.isArray(agents) && agents.length > 0;
}
